import { useEffect, useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface JobSource {
  id: string;
  name: string;
  country: string;
  industry: string | null;
  feed_url: string | null;
  feed_kind: string;
  active: boolean;
}

export function JobSourcesPage() {
  const [rows, setRows] = useState<JobSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    name: '',
    country: 'NO',
    industry: '',
    feed_url: '',
    feed_kind: 'rss',
  });

  function load() {
    api<{ data: JobSource[] }>('/admin/job-sources')
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }
  useEffect(load, []);

  async function update(id: string, patch: Partial<JobSource>) {
    try {
      await api(`/admin/job-sources/${id}`, { method: 'PATCH', json: patch });
      load();
    } catch {
      setError('Update failed.');
    }
  }
  async function remove(id: string) {
    if (!window.confirm('Delete this job source?')) return;
    try {
      await api(`/admin/job-sources/${id}`, { method: 'DELETE' });
      load();
    } catch {
      setError('Delete failed.');
    }
  }
  async function create() {
    setError(null);
    try {
      await api('/admin/job-sources', {
        method: 'POST',
        json: {
          name: draft.name,
          country: draft.country.toUpperCase(),
          industry: draft.industry.length > 0 ? draft.industry : null,
          feed_url: draft.feed_url.length > 0 ? draft.feed_url : null,
          feed_kind: draft.feed_kind,
        },
      });
      setDraft({ name: '', country: 'NO', industry: '', feed_url: '', feed_kind: 'rss' });
      load();
    } catch {
      setError('Create failed.');
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Job sources</h1>
        <p className="text-sm text-muted-foreground">
          Trusted feeds for the candidate "Relevant jobs" surface.
        </p>
      </header>
      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{rows?.length ?? 0} sources</CardTitle>
        </CardHeader>
        <CardContent>
          {rows === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {rows?.length === 0 && (
            <p className="text-sm text-muted-foreground">No job sources yet.</p>
          )}
          {rows !== null && rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((s) => (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border p-3 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-bold">
                      {s.name} <Badge variant="secondary">{s.country}</Badge>{' '}
                      {s.industry !== null && <Badge variant="secondary">{s.industry}</Badge>}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {s.feed_url ?? '—'} · {s.feed_kind}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void update(s.id, { active: !s.active })}
                    >
                      <Save className="mr-1 h-4 w-4" /> {s.active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void remove(s.id)}>
                      <Trash2 className="mr-1 h-4 w-4" /> Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add a job source</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
            onSubmit={(e) => {
              e.preventDefault();
              void create();
            }}
          >
            <Field
              label="Name"
              value={draft.name}
              onChange={(v) => setDraft({ ...draft, name: v })}
            />
            <Field
              label="Country (ISO)"
              value={draft.country}
              onChange={(v) => setDraft({ ...draft, country: v.toUpperCase() })}
            />
            <Field
              label="Industry"
              value={draft.industry}
              onChange={(v) => setDraft({ ...draft, industry: v })}
            />
            <Field
              label="Feed URL"
              value={draft.feed_url}
              onChange={(v) => setDraft({ ...draft, feed_url: v })}
            />
            <Field
              label="Feed kind"
              value={draft.feed_kind}
              onChange={(v) => setDraft({ ...draft, feed_kind: v })}
            />
            <div className="lg:col-span-5">
              <Button type="submit" disabled={draft.name === '' || draft.country.length !== 2}>
                <Plus className="mr-2 h-4 w-4" /> Add
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
